import { CSSProperties, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import { createContainer } from 'unstated-next'

import { colorBlender, getContrastRatio, isHexCode } from '@syconium/little-miss-figgy'

import { gql } from '../../__generated__/graphql/catalog'
import { useNextQuery } from '../../app/_hooks/useNextQuery'
import { usePreviewDirectives } from '../../app/_providers/PreviewDirectivesProvider.client'

type AccentColor = NonNullable<CSSProperties['color']>
type HexCodes = Array<AccentColor>
type HexCodeSetter = (arg1: HexCodes | undefined | null) => void

const useAccentColorContainer = (): {
  hexCodes: HexCodes | undefined | null
  setHexCodes: HexCodeSetter
} => {
  const { includeUnpublishedCmsContent } = usePreviewDirectives()
  const { data } = useNextQuery(
    gql(`
      query GetAccentColorDirective($handle: String!, $preview: Boolean = false) {
        siteFixtures(handle: $handle, preview: $preview) {
          __typename
          id
          enablePDPAccentColor
        }
      }
  `),
    {
      variables: {
        handle: 'default',
        preview: includeUnpublishedCmsContent,
      },
    }
  )

  const [hexCodes, setHexCodes] = useState<HexCodes | undefined | null>(undefined)

  return {
    hexCodes: data?.siteFixtures.enablePDPAccentColor ? hexCodes : hexCodes,
    setHexCodes,
  }
}

const AccentColorContainer = createContainer(useAccentColorContainer)
export const AccentColorProvider = AccentColorContainer.Provider

export const useAccentColorSetter = () => {
  return {
    setAccentColor: AccentColorContainer.useContainer().setHexCodes,
  }
}

export const useAccentColor = (): {
  accentColor: AccentColor | undefined
} => {
  const theme = useTheme()
  const { hexCodes: hexCodesContext } = AccentColorContainer.useContainer()

  const accentColor = useMemo<AccentColor | undefined>(() => {
    const scopedThemeBackgroundColor = theme.color.fill.background
    const hexCodes = hexCodesContext ?? []
    const hexCode = hexCodes
      .concat() // Make mutable then sort to start with the color closest to our desired contrast ratio. It is the least likely to need altering.
      .filter(hexCode => isHexCode(hexCode))
      .sort((thisHexCode, thatHexCode) => {
        const thisContrast = getContrastRatio(scopedThemeBackgroundColor, thisHexCode)
        const thatContrast = getContrastRatio(scopedThemeBackgroundColor, thatHexCode)
        return thatContrast - thisContrast
      })
      .slice(0, 1) // Only process the most likely to succeed color.
      .map(hexCode => {
        let contrast = getContrastRatio(scopedThemeBackgroundColor, hexCode)
        // If we pass 3:1, just use the color.
        if (contrast >= 3) {
          return hexCode
        } else {
          // The max we are OK with lightening/darkening to make this work is 66%. Otherwise the color becomes too hard to recognize.
          const lightnessChangeSteps = [
            0.11, -0.11, 0.22, -0.22, 0.33, -0.33, 0.44, -0.44, 0.55, -0.55, 0.66, -0.66,
          ]
          let acceptableHexResult: string | undefined = undefined
          lightnessChangeSteps.forEach(step => {
            if (!acceptableHexResult) {
              const alteredHex = colorBlender(step, hexCode, undefined, undefined) ?? hexCode
              const alteredContrast = getContrastRatio(scopedThemeBackgroundColor, alteredHex)
              if (alteredContrast >= 3) {
                acceptableHexResult = alteredHex
              }
            }
          })
          return acceptableHexResult
        }
      })[0]

    if (hexCode) {
      return hexCode
    } else {
      return undefined
    }
  }, [hexCodesContext, theme.color.fill.background])

  return {
    accentColor,
  }
}
